/**
 * SRES · Clean CPR Dashboard  (Sprint P37t · Feb 2026)
 * ═══════════════════════════════════════════════════════════════════
 *
 * ASYNC CPR GENERATION PIPELINE
 *
 *   Generate  →  POST /api/projects/{pid}/cpr/generate       → 202 { job_id }
 *   Poll      →  GET  /api/projects/{pid}/cpr/jobs/{jid}/status
 *   Download  →  GET  /api/projects/{pid}/cpr/{rid}/download
 *   Latest    →  GET  /api/projects/{pid}/cpr/latest         (resume)
 *   Active    →  GET  /api/projects/{pid}/cpr/jobs/active    (refresh recovery)
 *
 * The K8s ingress used to time out at ~60 s while the sync
 * `/export/pro-cpr.pdf` was compiling a 100+ page report. The async
 * pipeline returns immediately, and the browser polls until the
 * background worker persists the finished PDF to disk. No more 502s.
 *
 * State machine (canonical, single source of truth):
 *
 *   idle
 *     ├── click Generate → running
 *     └── click Regenerate → running
 *   running
 *     ├── job.job_status = "Completed" → done
 *     ├── job.job_status = "Failed"    → failed
 *     └── (auto-retry once)            → running
 *   done      — downloads enabled
 *   failed    — clear structured-error notice + primary [Try Again] button
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { toastSpecificReportReady } from "@/lib/reportToast";
import {
  CheckCircle, Circle, Warning, ArrowClockwise, Sparkle,
  FilePdf, FileDoc, Archive, Eye, Certificate, XCircle,
  UserCircle, X as XIcon, Cube,
} from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import ReportConfigPanel from "./components/ReportConfigPanel";
import MultiStandardPreviewModal from "./components/MultiStandardPreviewModal";
import CompetentPersonWorkspace from "@/pages/wizard/CompetentPersonWorkspace";
import CPApprovalModal from "./CPApprovalModal";       // P37m
import CPApprovalHistoryDrawer from "./CPApprovalHistoryDrawer"; // P37o
import BlockModelViewer from "@/pages/wizard/BlockModelViewer";
import CPRReportRunPanel from "./CPRReportRunPanel";   // Iter106 · restored governance panel
import FenceDiagramPicker from "./FenceDiagramPicker";   // Iter109 · CP fence-diagram wizard step
import ClassificationConfigPanel from "./components/ClassificationConfigPanel";  // Phase 1 · Feb 2026


// Fallback stage list (backend also returns `stages` in the status payload)
const DEFAULT_STAGES = [
  "Queued", "Preparing Data", "Validating Inputs", "Generating Narratives",
  "Rendering Charts", "Rendering GIS Maps", "Rendering Borehole Logs",
  "Rendering 3D Figures", "Building Appendices", "Compiling PDF",
  "Validating PDF", "Completed",
];

// Human-friendly labels for the structured error codes returned by
// /api/projects/{pid}/cpr/jobs/{jid}/status → error_code.
const ERROR_HELP = {
  CPR_JOB_TIMEOUT:          "The job took longer than the safety timeout. Please retry — SRES will resume from the last successful stage.",
  CPR_WORKER_CRASH:         "The background worker crashed unexpectedly. Retry the job; if it happens again, please contact support.",
  CPR_OUT_OF_MEMORY:        "The project was too large to render in one pass. Split large uploads or contact support to increase quota.",
  CPR_RENDER_FAILURE:       "SRES could not render one or more figures. Try again — the pipeline will retry the failed step.",
  CPR_GIS_PACKAGE_MISSING:  "GIS layers could not be prepared. Verify the uploaded boundary/collar files and retry.",
  CPR_BOREHOLE_PACKAGE_MISSING: "Borehole logs could not be prepared. Verify the uploaded collar/survey/assay files and retry.",
  CPR_VALIDATION_FAILED:    "The Publication Gate blocked the export. Please review the blocking items and try Draft first.",
  CPR_FILE_NOT_FOUND:       "The generated file could not be located. Please regenerate.",
  CPR_FILE_EMPTY:           "The generated file was empty. Please regenerate.",
  CPR_PDF_CORRUPT:          "The generated PDF failed validation. Please regenerate.",
};

// Local-storage key so a browser refresh mid-run resumes polling.
const jobStorageKey = (pid) => `sres.cpr.job.${pid}`;


// ═══════════════════════════════════════════════════════════════════
// Root component
// ═══════════════════════════════════════════════════════════════════
export default function CleanCPRDashboard({ projectId, project }) {
  const nav = useNavigate();
  const [phase, setPhase] = useState("idle");    // idle | running | done | failed
  const [jobId, setJobId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [retried, setRetried] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);   // Competent Person modal
  const [cpApprovalOpen, setCpApprovalOpen] = useState(false);  // P37m
  const [cpHistoryOpen, setCpHistoryOpen] = useState(false);    // P37o
  const [viewerOpen, setViewerOpen] = useState(false);          // 3D viewer
  const bootRef = useRef(false);   // idempotent boot-time recovery

  // Report configuration (shared between MainActionCard + DownloadPanel)
  const [reportConfig, setReportConfig] = useState({
    report_type:      "CPR",
    primary_standard: "JORC_2012",
    report_status:    "DRAFT",
    llm_chapters:     false,
  });
  const handleConfigChange = useCallback((c) => setReportConfig(c), []);

  const workflowQ = useQuery({
    queryKey: ["clean-cpr-status", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/workflow-status`).then((r) => r.data),
    enabled:  !!projectId,
    staleTime: 15_000,
  });

  // ── Boot-time recovery ────────────────────────────────────────────
  // 1. If localStorage has a job id from before a refresh → resume polling.
  // 2. Else if the backend reports an active job for this project → resume.
  // 3. Else fetch the latest completed report so the Download panel
  //    stays available even without a fresh generation run.
  useEffect(() => {
    if (!projectId || bootRef.current) return;
    bootRef.current = true;

    let cancelled = false;
    (async () => {
      const saved = localStorage.getItem(jobStorageKey(projectId));
      if (saved) {
        setJobId(saved);
        setPhase("running");
        return;
      }
      try {
        const { data: active } = await api.get(
          `/projects/${projectId}/cpr/jobs/active`);
        if (!cancelled && active?.job_id) {
          localStorage.setItem(jobStorageKey(projectId), active.job_id);
          setJobId(active.job_id);
          setPhase("running");
          return;
        }
      } catch { /* ignore — endpoint optional */ }

      try {
        const { data: latest } = await api.get(
          `/projects/${projectId}/cpr/latest`);
        if (!cancelled && latest?.id) {
          setReportId(latest.id);
          setPhase("done");
        }
      } catch { /* no completed report yet — stay idle */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ── Generate mutation ────────────────────────────────────────────
  const startMut = useMutation({
    mutationFn: () => api.post(
      `/projects/${projectId}/cpr/generate`,
      {
        status:            reportConfig.report_status || "DRAFT",
        report_type:       reportConfig.report_type || "CPR",
        primary_standard:  reportConfig.primary_standard || "JORC_2012",
        secondary_standard: reportConfig.secondary_standard || "",
        llm_chapters:      !!reportConfig.llm_chapters,
      },
      { timeout: 20_000 },
    ).then((r) => r.data),
    retry: 0,
    onSuccess: (d) => {
      if (!d.job_id) {
        toast.error("Could not enqueue CPR job — please retry.");
        setPhase("failed");
        return;
      }
      localStorage.setItem(jobStorageKey(projectId), d.job_id);
      setJobId(d.job_id);
      setReportId(null);
      setPhase("running");
      toast.info(d.coalesced
        ? "SRES AI is already preparing a CPR — resuming that run."
        : "SRES AI is preparing your CPR…");
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || "Failed to start CPR generation";
      toast.error(msg);
      setPhase("failed");
    },
  });

  // ── Poll job status ──────────────────────────────────────────────
  const jobQ = useQuery({
    queryKey: ["clean-cpr-job", projectId, jobId],
    queryFn:  () => api.get(
      `/projects/${projectId}/cpr/jobs/${jobId}/status`,
      { timeout: 15_000 },
    ).then((r) => r.data),
    enabled: phase === "running" && !!jobId,
    refetchInterval: (q) => {
      if (phase !== "running") return false;
      const j = q?.state?.data;
      const pct = j?.progress ?? 0;
      // Adaptive: fast at the start (giving early feedback), slower later
      // (reducing ingress load during the long PDF compile stage).
      if (pct < 25) return 1200;
      if (pct < 70) return 2000;
      return 3500;
    },
    retry: 3,
    retryDelay: 2000,
    refetchOnWindowFocus: true,
  });

  // ── React to job state changes ───────────────────────────────────
  useEffect(() => {
    if (!jobQ.data) return;
    const j = jobQ.data;
    if (j.job_status === "Completed") {
      setPhase("done");
      setReportId(j.report_id || null);
      localStorage.removeItem(jobStorageKey(projectId));
      toastSpecificReportReady({
        projectId, navigate: nav,
        kind: (reportConfig.report_type || "Report"),
        reportId: j.report_id,
      });
    } else if (j.job_status === "Failed") {
      // Auto-retry once for transient failures. Structural errors
      // (VALIDATION_FAILED, PDF_CORRUPT) skip the auto-retry so the
      // user actually sees the diagnostic.
      const soft = !j.error_code
        || j.error_code === "CPR_RENDER_FAILURE"
        || j.error_code === "CPR_JOB_TIMEOUT"
        || j.error_code === "CPR_WORKER_CRASH";
      if (soft && !retried) {
        setRetried(true);
        toast.info("SRES AI is retrying automatically…");
        setTimeout(() => startMut.mutate(), 1200);
      } else {
        setPhase("failed");
      }
    }
  }, [jobQ.data, retried, startMut, projectId, nav, reportConfig.report_type]);

  if (workflowQ.isLoading) return <LoadingSkeleton />;
  if (workflowQ.isError)   return <ErrorState error={workflowQ.error} />;

  const status = workflowQ.data || {};

  // ── Canonical report state ────────────────────────────────────────
  const reportState = _reportState({ phase, status, job: jobQ.data });

  return (
    <div className="space-y-4 max-w-5xl" data-testid="clean-cpr-root">
      {/* ── SECTION A — Project Summary ─────────────────────────── */}
      <ProjectSummaryCard status={status} project={project}
                             reportState={reportState}
                             onEditCP={() => setCpOpen(true)}
                             onCPApproval={() => setCpApprovalOpen(true)}
                             onCPHistory={() => setCpHistoryOpen(true)}
                             onView3D={() => setViewerOpen(true)} />

      {/* ── SECTION B — SRES AI Status message ──────────────────── */}
      <AIStatusMessage phase={phase} status={status}
                          reportState={reportState} />

      {/* ── SECTION B.1 — Report Configuration (P37c) ────────────── */}
      <ReportConfigPanel projectId={projectId} onChange={handleConfigChange} />

      {/* ── SECTION B.2 — Classification thresholds (Phase 1 · Feb 2026).
           Only shown when the CP has opted into the Extended CPR path
           so we don't distract with premium tuning on standard runs. */}
      {String(reportConfig.report_type || "").toUpperCase() === "EXTENDED" && (
        <ClassificationConfigPanel projectId={projectId} />
      )}

      {/* ── SECTION C — Main Action / Progress / Download ───────── */}
      {phase === "idle" && (
        <MainActionCard
          onGenerate={() => { setRetried(false); startMut.mutate(); }}
          busy={startMut.isPending}
          reportState={reportState}
        />
      )}
      {phase === "running" && (
        <ProgressPanel job={jobQ.data} />
      )}
      {phase === "done" && (
        <DownloadPanel
          projectId={projectId}
          project={project}
          reportId={reportId}
          reportState={reportState}
          reportConfig={reportConfig}
          onRegenerate={() => {
            setRetried(false);
            setReportId(null);
            setPhase("idle");
            setJobId(null);
            localStorage.removeItem(jobStorageKey(projectId));
            workflowQ.refetch();
          }}
        />
      )}
      {phase === "failed" && (
        <FailedPanel
          onRetry={() => { setRetried(false); startMut.mutate(); }}
          job={jobQ.data}
        />
      )}

      {/* ── SECTION D — Locked Report Run governance ─────────────
          Iter106 · restored after Iter105 regression. This panel
          owns the immutable report-run lifecycle: open/successor
          runs, blocking-validation review, publish gate, live
          compile log, download and preview of the final PDF. */}
      <CPRReportRunPanel pid={projectId} />

      {/* ── SECTION E — Fence Diagram Appendix (Iter109) ─────────
          CP-facing picker: nominate any ≥ 3 holes and the fence
          diagram is auto-included in the next CPR compile using the
          same swim-lane engine as the per-hole logs. */}
      <FenceDiagramPicker pid={projectId} />

      {/* Competent Person modal (dockable full-screen overlay) */}
      <CompetentPersonModal
        open={cpOpen}
        onClose={() => setCpOpen(false)}
        projectId={projectId}
      />

      {/* P37m · CP Approval workflow modal */}
      <CPApprovalModal
        open={cpApprovalOpen}
        onClose={() => setCpApprovalOpen(false)}
        projectId={projectId}
        project={project}
      />

      {/* P37o · Immutable CP approval history drawer */}
      <CPApprovalHistoryDrawer
        open={cpHistoryOpen}
        onClose={() => setCpHistoryOpen(false)}
        projectId={projectId}
        project={project}
      />

      {/* Interactive 3D block-model viewer (WebGL, R3F) */}
      {viewerOpen && (
        <BlockModelViewer
          projectId={projectId}
          cutoff={project?.reporting_cutoff}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Competent Person Modal (wraps CompetentPersonWorkspace as full overlay)
// ═══════════════════════════════════════════════════════════════════
function CompetentPersonModal({ open, onClose, projectId }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="clean-cpr-cp-modal"
    >
      <div className="max-w-6xl mx-auto rounded-lg bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-brand-blue/10 to-transparent sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <UserCircle size={22} weight="duotone" className="text-brand-blue" />
            <h2 className="text-[15px] font-bold text-slate-800">
              Competent Person Details
            </h2>
            <span className="text-[11px] text-slate-500 hidden sm:inline">
              — profiles · responsibilities · site visits · documents · declarations
            </span>
          </div>
          <Button
            variant="outline" size="sm" onClick={onClose}
            className="h-8 text-[11px]"
            data-testid="clean-cpr-cp-modal-close"
          >
            <XIcon size={12} className="mr-1" /> Close
          </Button>
        </div>
        <div className="p-4 sm:p-6">
          <CompetentPersonWorkspace projectId={projectId} />
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Canonical report state
// ═══════════════════════════════════════════════════════════════════
function _reportState({ phase, status, job }) {
  const kpis   = status.kpis || {};
  const modules = status.modules || {};
  const collarModule = modules.collar || modules.data_upload || {};
  const hasData  = (kpis.completed ?? 0) > 0 && (kpis.critical_errors ?? 0) === 0
                 && (collarModule.rows ?? kpis.collar_rows ?? 1) !== 0;
  const hasModel = modules.block_model?.status === "consumed"
                || modules.block_model?.status === "ready";
  const readinessPct = status.readiness_percentage ?? 0;

  if (phase === "running") return {
    label: job?.current_stage || "Preparing CPR",
    key:   "running",
    tone:  "sky",
  };
  if (phase === "done") {
    if (!hasData) return {
      label: "Draft Generated (data limitation)",
      key: "draft",
      tone: "amber",
    };
    return {
      label: hasModel && readinessPct >= 80
        ? "Awaiting CP Confirmation"
        : "Draft Generated",
      key: hasModel && readinessPct >= 80 ? "awaiting_cp" : "draft",
      tone: hasModel && readinessPct >= 80 ? "sky" : "amber",
    };
  }
  if (phase === "failed") return {
    label: "Failed — Material Data Missing",
    key: "failed",
    tone: "rose",
  };
  // idle
  if (!hasData) return {
    label: "Awaiting Project Data",
    key: "awaiting_data",
    tone: "amber",
  };
  if (!hasModel) return {
    label: "Ready to Build Resource Model",
    key: "ready",
    tone: "sky",
  };
  return {
    label: "Ready to Generate Report",
    key: "ready",
    tone: "emerald",
  };
}


// ═══════════════════════════════════════════════════════════════════
// SECTION A — Project Summary
// ═══════════════════════════════════════════════════════════════════
function ProjectSummaryCard({ status, project, reportState, onEditCP, onCPApproval, onCPHistory, onView3D }) {
  const p = project || {};
  const rows = [
    { label: "Project",           value: p.name || "—" },
    { label: "Commodity",          value: p.commodity ? `${_commodityName(p.commodity)} (${p.commodity})` : "—" },
    { label: "Country",           value: p.country || "—" },
    { label: "Standard",          value: p.reporting_standard || "JORC 2012" },
    { label: "Competent Person",  value: p.competent_person || "Not assigned" },
    { label: "Reporting Cutoff",  value: p.cutoff_grade
                                             ? `${p.cutoff_grade} ${p.grade_unit || "g/t"}`
                                             : "—" },
  ];

  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky:     "bg-sky-50 text-sky-800 border-sky-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
  }[reportState.tone] || "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden"
         data-testid="clean-cpr-summary">
      <div className="px-5 py-3 bg-gradient-to-r from-brand-blue/5 to-transparent border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <Certificate size={18} weight="duotone" className="text-brand-blue" />
        <h2 className="text-[15px] font-bold text-slate-800 flex-1 min-w-0 truncate">
          {p.name || "Competent Person's Report"}
        </h2>
        <span className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider ${toneCls}`}
              data-testid="clean-cpr-report-state">
          {reportState.label}
        </span>
        {onEditCP && (
          <Button
            variant="outline" size="sm" onClick={onEditCP}
            className="h-8 text-[11px] font-semibold border-brand-blue/40 text-brand-blue hover:bg-brand-blue/10"
            data-testid="clean-cpr-edit-cp-btn"
          >
            <UserCircle size={12} className="mr-1" weight="duotone" />
            Edit Competent Person Details
          </Button>
        )}
        {onCPApproval && (
          <Button
            variant="outline" size="sm" onClick={onCPApproval}
            className="h-8 text-[11px] font-semibold border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"
            data-testid="clean-cpr-cp-approval-btn"
          >
            <Certificate size={12} className="mr-1" weight="duotone" />
            CP Approval Workflow
          </Button>
        )}
        {onCPHistory && (
          <Button
            variant="outline" size="sm" onClick={onCPHistory}
            className="h-8 text-[11px] font-semibold border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="clean-cpr-cp-history-btn"
          >
            <Certificate size={12} className="mr-1" />
            Approval History
          </Button>
        )}
        {onView3D && (
          <Button
            variant="outline" size="sm" onClick={onView3D}
            className="h-8 text-[11px] font-semibold border-cyan-600/40 text-cyan-700 hover:bg-cyan-50"
            data-testid="clean-cpr-view-3d-btn"
          >
            <Cube size={12} className="mr-1" weight="duotone" />
            Open 3D Block-Model Viewer
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 p-5">
        {rows.map((r) => (
          <div key={r.label} data-testid={`clean-cpr-kv-${_slug(r.label)}`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">
              {r.label}
            </div>
            <div className="text-[13px] font-semibold text-slate-900 truncate"
                 title={r.value}>
              {r.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SECTION B — SRES AI Status message
// ═══════════════════════════════════════════════════════════════════
function AIStatusMessage({ phase, status, reportState }) {
  const message = useMemo(() => {
    if (phase === "running")  return "SRES AI is checking your project, resolving safe data issues and preparing the complete CPR package.";
    if (phase === "done")     return "Your CPR is ready. Please download the report using the links below.";
    if (phase === "failed")   return "SRES AI was unable to complete the CPR because a material data limitation was detected.";
    if (reportState.key === "awaiting_data")
      return "SRES AI will check your project and generate the maximum technically valid Draft CPR when you start.";
    if (reportState.key === "ready")
      return "Your project looks ready. Click Check, Fix & Generate Report — SRES AI will handle everything automatically (CPR remains one of the report types).";
    return "SRES AI is ready to work on your project.";
  }, [phase, reportState.key]);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 flex gap-3"
         data-testid="clean-cpr-ai-status">
      <div className="w-9 h-9 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
        <Sparkle size={18} weight="fill" className="text-brand-blue" />
      </div>
      <div className="flex-1">
        <div className="text-[12px] font-bold text-slate-800">SRES AI</div>
        <div className="text-[12.5px] text-slate-700 mt-0.5">{message}</div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// SECTION C — Main Action
// ═══════════════════════════════════════════════════════════════════
function MainActionCard({ onGenerate, busy, reportState }) {
  return (
    <div className="rounded-lg border-2 border-brand-blue/30 bg-gradient-to-br from-brand-blue/5 via-white to-transparent shadow-sm p-6"
         data-testid="clean-cpr-action">
      <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
        <div className="text-center sm:text-left">
          <h3 className="text-[16px] font-bold text-slate-800">Ready to generate your CPR?</h3>
          <p className="text-[12px] text-slate-600 mt-1 max-w-xl">
            One click. SRES AI will detect all available project data, safely repair issues,
            rebuild missing derived outputs, generate every figure, GIS map and narrative,
            then compile the PDF, DOCX and ZIP bundle.
          </p>
        </div>
        <Button
          className="h-12 px-6 text-[13px] font-bold uppercase tracking-widest bg-brand-blue hover:bg-brand-blue/90 text-white shadow shrink-0"
          onClick={onGenerate} disabled={busy}
          data-testid="clean-cpr-generate-btn">
          {busy
            ? (<><ArrowClockwise size={16} className="mr-2 animate-spin" /> Starting…</>)
            : (<><Sparkle size={16} weight="fill" className="mr-2" /> Check, Fix &amp; Generate Report</>)}
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Progress panel (during run) — uses backend `current_stage` + `progress`
// ═══════════════════════════════════════════════════════════════════
function ProgressPanel({ job }) {
  const stages = (job?.stages && job.stages.length) ? job.stages : DEFAULT_STAGES;
  const cur    = job?.current_stage || stages[0];
  const pct    = Math.min(100, Math.max(0, job?.progress ?? 0));
  const curIdx = Math.max(0, stages.indexOf(cur));

  // Elapsed time — tracks from mount (first paint of the panel).
  const [elapsedS, setElapsedS] = useState(0);
  useEffect(() => {
    // If the backend already knows a started_at, we can back-fill the
    // elapsed clock so a browser refresh doesn't reset it to 0.
    const initialStart = job?.started_at
      ? Math.floor((Date.now() - new Date(job.started_at).getTime()) / 1000)
      : 0;
    setElapsedS(Math.max(0, initialStart));
    const t = setInterval(() => setElapsedS((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [job?.started_at]);
  const mm = Math.floor(elapsedS / 60);
  const ss = String(elapsedS % 60).padStart(2, "0");

  const nDone = curIdx;      // completed stages before current
  const nTotal = stages.length;

  return (
    <div className="rounded-lg border-2 border-brand-blue/40 bg-white shadow-sm p-6"
         data-testid="clean-cpr-progress">
      <div className="flex items-center gap-3 mb-4">
        <ArrowClockwise size={22} className="text-brand-blue animate-spin" />
        <div className="flex-1">
          <h3 className="text-[15px] font-bold text-slate-800">Preparing your CPR…</h3>
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid="clean-cpr-current-stage">
            {cur} — this usually takes 30–90 seconds.
          </p>
        </div>
        <div className="text-[20px] font-bold text-brand-blue"
             data-testid="clean-cpr-progress-pct">
          {pct.toFixed(0)}%
        </div>
      </div>
      <div className="w-full h-2 rounded bg-slate-100 overflow-hidden mb-4">
        <div className="h-full bg-brand-blue transition-all rounded"
             style={{ width: `${pct}%` }} />
      </div>
      {/* Iter97 · Feb 2026 · soft-warning at 80% of the compile
          timeout so users see friendly progress feedback instead of a
          silent hang. Timeout defaults to 300 s (SRES_COMPILE_TIMEOUT_S). */}
      {(() => {
        const timeoutS = Number(job?.compile_timeout_s || 300);
        const warnAt   = timeoutS * 0.8;
        if (elapsedS < warnAt) return null;
        const remaining = Math.max(0, timeoutS - elapsedS);
        const critical  = elapsedS >= timeoutS * 0.95;
        return (
          <div className={`mb-4 rounded-md border px-3 py-2 flex items-start gap-2
                            ${critical
                              ? "border-rose-300 bg-rose-50 text-rose-900"
                              : "border-amber-300 bg-amber-50 text-amber-900"}`}
                data-testid="clean-cpr-timeout-warning"
                data-severity={critical ? "critical" : "warning"}>
            <span className="text-[14px] leading-none">⏱</span>
            <div className="flex-1 min-w-0 text-[11.5px]">
              <span className="font-semibold">
                {critical ? "Almost timing out · " : "Taking longer than expected · "}
              </span>
              we&apos;ll auto-fail the run at {Math.round(timeoutS / 60)} min so you
              can click Retry. {remaining > 0 && <span className="font-mono">~{remaining}s left.</span>}
            </div>
          </div>
        );
      })()}
      {/* ── Live meta strip ────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <MetaChip label="ELAPSED"  value={`${mm}:${ss}`} tone="info" testid="clean-cpr-elapsed" />
        <MetaChip label="STAGE"    value={`${nDone}/${nTotal}`} tone="ok" testid="clean-cpr-done" />
        <MetaChip label="STATUS"   value={job?.job_status || "Queued"} tone="info" testid="clean-cpr-jstatus" />
        <MetaChip label="RETRY"    value={String(job?.retry_count || 0)} tone={job?.retry_count ? "warn" : "ok"} testid="clean-cpr-retry-count" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {stages.map((s, i) => {
          const isDone    = i < curIdx;
          const isCurrent = i === curIdx;
          const tone = isDone    ? "text-emerald-700 bg-emerald-50"
                     : isCurrent ? "text-brand-blue bg-sky-50 font-semibold"
                                  : "text-slate-500 bg-slate-50/40";
          const stateLabel = isDone    ? "Complete"
                            : isCurrent ? "In Progress"
                                         : "Waiting";
          return (
            <div key={s}
                 className={`flex items-center gap-2 text-[11.5px] py-1 px-2 rounded ${tone}`}
                 data-testid={`clean-cpr-step-${_slug(s)}`}
                 data-status={isDone ? "done" : isCurrent ? "running" : "pending"}>
              {isDone    && <CheckCircle size={12} weight="fill" />}
              {isCurrent && <ArrowClockwise size={12} className="animate-spin" />}
              {!isDone && !isCurrent && <Circle size={12} />}
              <span className="flex-1 truncate">{s}</span>
              <span className="text-[10px] uppercase tracking-widest opacity-70">
                {stateLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Download panel (post-completion) — uses async /cpr/{report_id}/download
// ═══════════════════════════════════════════════════════════════════
function DownloadPanel({ projectId, project, reportId, reportState, reportConfig, onRegenerate }) {
  const [busy, setBusy] = useState(null);
  const [meta, setMeta] = useState({ pdf: null, docx: null, zip: null, gis: null });
  const [msOpen, setMsOpen] = useState(false);

  // Metadata about the completed report (page count, sha256, size)
  const latestQ = useQuery({
    queryKey: ["cpr-latest", projectId, reportId],
    queryFn:  () => api.get(`/projects/${projectId}/cpr/latest`).then((r) => r.data),
    enabled:  !!projectId,
    staleTime: 60_000,
  });
  const rid = reportId || latestQ.data?.id || null;
  const reportInfo = latestQ.data;

  const dlPro = async () => {
    if (!rid) { toast.error("No completed report — please generate first."); return; }
    setBusy("pdf");
    try {
      const res = await api.get(
        `/projects/${projectId}/cpr/${rid}/download`,
        { responseType: "blob", timeout: 120_000 });
      const size = res.data.size;
      setMeta((m) => ({ ...m, pdf: { size, at: new Date() } }));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"];
      a.download = cd?.split("filename=")[1]?.replace(/"/g, "")
                   || `${(project?.name || "Project").replace(/\s+/g, "_")}_CPR.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed");
    } finally { setBusy(null); }
  };

  const dlDocx = async () => {
    setBusy("docx");
    try {
      const res = await api.get(
        `/projects/${projectId}/export/report.docx`,
        { responseType: "blob", timeout: 180_000 });
      const size = res.data.size;
      setMeta((m) => ({ ...m, docx: { size, at: new Date() } }));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(project?.name || "Project").replace(/\s+/g, "_")}_CPR.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed");
    } finally { setBusy(null); }
  };

  const dlZip = async () => {
    setBusy("zip");
    try {
      const res = await api.get(`/projects/${projectId}/archive.zip`,
                                  { responseType: "blob", timeout: 240_000 });
      const size = res.data.size;
      setMeta((m) => ({ ...m, zip: { size, at: new Date() } }));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(project?.name || "Project").replace(/\s+/g, "_")}_CPR_Bundle.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed");
    } finally { setBusy(null); }
  };

  const preview = async () => {
    if (!rid) { toast.error("No completed report — please generate first."); return; }
    setBusy("preview");
    try {
      const res = await api.get(
        `/projects/${projectId}/cpr/${rid}/download`,
        { responseType: "blob", timeout: 120_000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.message || "Preview failed");
    } finally { setBusy(null); }
  };

  // GIS complete-package.zip (18-map bundle)
  const dlGis = async () => {
    setBusy("gis");
    try {
      const res = await api.get(
        `/projects/${projectId}/gis/complete-package.zip`,
        { responseType: "blob", timeout: 240_000 });
      const size = res.data.size;
      setMeta((m) => ({ ...m, gis: { size, at: new Date() } }));
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(project?.name || "Project").replace(/\s+/g, "_")}_GIS_Complete.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("GIS bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Download failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50/70 to-white shadow-sm p-6"
         data-testid="clean-cpr-download">
      <div className="flex items-start gap-3 mb-4">
        <CheckCircle size={28} weight="fill" className="text-emerald-600 shrink-0" />
        <div className="flex-1">
          <h3 className="text-[16px] font-bold text-slate-800">CPR Generated Successfully</h3>
          <p className="text-[12px] text-slate-600 mt-0.5">
            Project: <b>{project?.name || "—"}</b> · Status: <b>{reportState.label}</b>
            {reportInfo?.page_count && (
              <> · Pages: <b>{reportInfo.page_count}</b></>
            )}
            {reportInfo?.file_size_bytes && (
              <> · Size: <b>{_fmtSize(reportInfo.file_size_bytes)}</b></>
            )}
            {reportInfo?.revision && (
              <> · Revision: <b>Rev{String(reportInfo.revision).padStart(2, "0")}</b></>
            )}
          </p>
          {reportInfo?.sha256 && (
            <p className="text-[10px] font-mono text-slate-400 mt-0.5" data-testid="clean-cpr-sha256">
              SHA-256: {reportInfo.sha256.slice(0, 16)}…
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onRegenerate}
                className="h-9 text-[11px] font-bold uppercase tracking-widest"
                data-testid="clean-cpr-regenerate-btn">
          <ArrowClockwise size={12} className="mr-1.5" /> Regenerate CPR
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 mt-3">
        <DownloadTile Icon={FilePdf} label="Download PDF" tone="rose"
                       onClick={dlPro} busy={busy === "pdf"}
                       disabled={!rid}
                       meta={meta.pdf}
                       testid="clean-cpr-dl-pdf" />
        <DownloadTile Icon={FileDoc} label="Download DOCX" tone="sky"
                       onClick={dlDocx} busy={busy === "docx"}
                       meta={meta.docx}
                       testid="clean-cpr-dl-docx" />
        <DownloadTile Icon={Archive} label="Download ZIP Bundle" tone="amber"
                       onClick={dlZip} busy={busy === "zip"}
                       meta={meta.zip}
                       testid="clean-cpr-dl-zip" />
        <DownloadTile Icon={Archive} label="GIS Package (18 maps)" tone="emerald"
                       onClick={dlGis} busy={busy === "gis"}
                       meta={meta.gis}
                       testid="clean-cpr-dl-gis" />
        <DownloadTile Icon={Certificate} label="Multi-Standard Preview" tone="violet"
                       onClick={() => setMsOpen(true)}
                       testid="clean-cpr-ms-preview" />
        <DownloadTile Icon={Eye}     label="Preview Report" tone="slate"
                       onClick={preview} busy={busy === "preview"}
                       disabled={!rid}
                       testid="clean-cpr-preview" />
      </div>

      <MultiStandardPreviewModal
        open={msOpen} onClose={() => setMsOpen(false)}
        projectId={projectId} project={project} />
    </div>
  );
}


function DownloadTile({ Icon, label, tone, onClick, busy, meta, testid, disabled }) {
  const toneCls = {
    rose:     "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800",
    sky:      "border-sky-200 bg-sky-50 hover:bg-sky-100 text-sky-800",
    amber:    "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800",
    emerald:  "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800",
    violet:   "border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-800",
    slate:    "border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800",
  }[tone];
  return (
    <button onClick={onClick} disabled={busy || disabled} data-testid={testid}
            className={`rounded-md border-2 ${toneCls} p-3 flex items-start gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed text-left`}>
      {busy ? <ArrowClockwise size={20} className="animate-spin mt-0.5" /> : <Icon size={20} weight="duotone" className="mt-0.5" />}
      <div className="flex-1">
        <div className="text-[12.5px] font-bold">{label}</div>
        {meta && (
          <div className="text-[10px] opacity-70 mt-0.5">
            {_fmtSize(meta.size)} · {meta.at.toLocaleTimeString()}
          </div>
        )}
      </div>
    </button>
  );
}


function MetaChip({ label, value, tone, testid }) {
  const toneCls = {
    info:  "border-slate-200  bg-slate-50   text-slate-700",
    ok:    "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn:  "border-amber-200  bg-amber-50   text-amber-800",
    bad:   "border-rose-200   bg-rose-50    text-rose-700",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`rounded-md border ${toneCls} px-2 py-1.5`}
         data-testid={testid}>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-[13px] font-bold leading-tight mt-0.5">{value}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Failed panel — structured error-code aware
// ═══════════════════════════════════════════════════════════════════
function FailedPanel({ onRetry, job }) {
  const code = job?.error_code;
  const message = job?.error_message
    || ERROR_HELP[code]
    || "A material technical limitation was detected.";
  const help = code && ERROR_HELP[code] ? ERROR_HELP[code] : null;

  return (
    <div className="rounded-lg border-2 border-rose-300 bg-gradient-to-br from-rose-50/70 to-white shadow-sm p-6"
         data-testid="clean-cpr-failed">
      <div className="flex items-start gap-3 mb-4">
        <XCircle size={28} weight="fill" className="text-rose-600 shrink-0" />
        <div className="flex-1">
          <h3 className="text-[16px] font-bold text-slate-800">
            SRES AI could not complete the CPR.
          </h3>
          {code && (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-[10.5px] font-mono uppercase tracking-widest bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded"
                    data-testid="clean-cpr-error-code">
                {code}
              </span>
              {job?.current_stage && (
                <span className="text-[10.5px] text-slate-500">
                  at <b>{job.current_stage}</b>
                </span>
              )}
            </div>
          )}
          <p className="text-[12.5px] text-rose-800 font-semibold mt-2"
             data-testid="clean-cpr-error-message">
            {message}
          </p>
          {help && help !== message && (
            <p className="text-[11.5px] text-slate-600 mt-1.5">{help}</p>
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            Please review the underlying data. SRES AI will retry safely
            when you press Try Again.
          </p>
        </div>
        <Button
          className="h-10 px-4 text-[12px] font-bold uppercase tracking-widest bg-brand-blue hover:bg-brand-blue/90 text-white shrink-0"
          onClick={onRetry}
          data-testid="clean-cpr-retry-btn">
          <ArrowClockwise size={14} className="mr-1.5" /> Try Again
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════
function LoadingSkeleton() {
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-6 animate-pulse"
         data-testid="clean-cpr-loading">
      <div className="h-8 bg-slate-100 rounded w-1/3 mb-3" />
      <div className="grid grid-cols-3 gap-2 mb-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 bg-slate-100 rounded" />
        ))}
      </div>
      <div className="h-32 bg-slate-100 rounded" />
    </div>
  );
}


function ErrorState({ error }) {
  return (
    <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 text-rose-800 text-sm"
         data-testid="clean-cpr-error">
      Failed to load CPR readiness. {error?.message}
    </div>
  );
}


function _slug(s) {
  return (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}


function _commodityName(sym) {
  const m = {
    Au: "Gold", Ag: "Silver", Cu: "Copper", Pb: "Lead", Zn: "Zinc",
    Ni: "Nickel", Fe: "Iron", U: "Uranium", Sn: "Tin", Mo: "Molybdenum",
    W: "Tungsten", Li: "Lithium",
  };
  return m[sym] || sym;
}


function _fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}
